import { AfterViewInit, Component, Input, OnInit } from '@angular/core';
import { AsyncValidatorFn, FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { I18nService } from '@app/i18n/services';
import { AuthService } from '@services/auth/';
import { FormsService } from '@services/forms/';
import { FormAsyncValidators } from '@tcp13equals2/z5-lib';
import { componentDestroyed, OnDestroyMixin } from '@w11k/ngx-componentdestroyed';
import find from 'lodash/find';
import isNil from 'lodash/isNil';
import { takeUntil, tap } from 'rxjs/operators';

import { FormEditorInputType, FormValidatorType, IFormEditorData, IFormEditorRow, IFormEditorSelectOption } from '../_models';

@Component({
	selector: 'app-form-editor',
	templateUrl: './form-editor.component.html',
	styleUrls: ['./_style.scss']
})
export class FormEditorComponent extends OnDestroyMixin implements OnInit, AfterViewInit {
	@Input() formdata: IFormEditorData;

	private i18nNotSet: string = this.i18nService.get('Not set', 'app.descriptor.not-set');

	formEditorInputType = FormEditorInputType;

	editing = false;
	formIsValid = false;
	formInputs: FormGroup = new FormGroup({});
	formInputsCreated = false;

	constructor(
		private formsService: FormsService,
		private authService: AuthService,
		private i18nService: I18nService,
		private formAsyncValidators: FormAsyncValidators
	) {
		super();
	}

	ngOnInit(): void {
		this.formdata.rows.forEach((row: IFormEditorRow) => {
			const validators: ValidatorFn[] = [];
			const asyncValidators: AsyncValidatorFn[] = [];
			row.validators.forEach((validator: FormValidatorType) => {
				switch (validator) {
					case FormValidatorType.email:
						validators.push(Validators.email);
						break;
					case FormValidatorType.required:
						validators.push(Validators.required);
						break;
					case FormValidatorType.password:
						asyncValidators.push(this.formAsyncValidators.password.bind(this.formAsyncValidators));
						break;
					case FormValidatorType.confirmPassword:
						validators.push(this.formsService.confirmPasswordMatcher());
						break;
					case FormValidatorType.uniq_email:
						asyncValidators.push(this.formsService.emailAvailable(row.valids));
						break;
					case FormValidatorType.validUrlFormat:
						validators.push(this.formsService.urlFormatValidator);
						break;
					case FormValidatorType.validUrlCheck:
						asyncValidators.push(this.formsService.urlValid(row.valids));
						break;
				}
			});

			this.formInputs.addControl(row.key, new FormControl('', validators, asyncValidators));
			this.formInputs.get(row.key).setValue(row.value);
			if (row.editable) {
				this.formInputs.get(row.key).enable();
			} else {
				this.formInputs.get(row.key).disable();
			}
		});
		this.formInputsCreated = true;
	}

	ngAfterViewInit(): void {
		this.formInputs.valueChanges
			.pipe(takeUntil(componentDestroyed(this)))
			.pipe(
				tap((_) => {
					this.formInputs.markAllAsTouched();
				})
			)
			.subscribe(this.checkFormCompletion.bind(this));
		this.formInputs.statusChanges.pipe(takeUntil(componentDestroyed(this))).subscribe(this.checkFormCompletion.bind(this));
	}

	private checkFormCompletion(x: any) {
		this.formIsValid = x === 'VALID';
	}

	getDisplayLabel(row: IFormEditorRow): string {
		if (row.type === FormEditorInputType.select) {
			const option: IFormEditorSelectOption = row.options.find((o) => o.value === row.value);
			if (option) {
				return option.label;
			}
		}
		if (row.value && row.value !== '') {
			return row.value;
		} else {
			return this.i18nNotSet;
		}
	}

	controlSame(field: string): boolean {
		let same = true;
		const row: IFormEditorRow = find(this.formdata.rows, (r) => {
			return r.key === field;
		});
		const control = this.formInputs.get(row.key);
		if (control && row.editable && control.value !== row.value) {
			same = false;
		}
		return same;
	}

	formValidAndChanged(): boolean {
		// check if changed
		let hasChanged = false;
		this.formdata.rows.forEach((row: IFormEditorRow) => {
			const control = this.formInputs.get(row.key);
			if (control && row.editable && control.value !== row.value) {
				hasChanged = true;
			}
		});
		return !this.formInputs.invalid && hasChanged;
	}

	showPasswordError(): boolean {
		const control = this.formInputs.get('password');
		return control && control.touched && control.hasError('pattern') && control.status === 'INVALID';
	}
	showPasswordSuccess(): boolean {
		const control = this.formInputs.get('password');
		return control && control.status === 'VALID';
	}

	resetControl(field: string) {
		const control = this.formInputs.get(field);
		let origValue: any;
		const row: IFormEditorRow = find(this.formdata.rows, (r) => {
			return r.key === field;
		});
		if (row) {
			origValue = row.value;
		}
		if (control && !isNil(origValue)) {
			control.reset(origValue);
		}
	}

	editForm() {
		this.editing = true;
	}

	closeForm() {
		this.editing = false;
	}

	saveForm() {
		this.editing = false;
		const updatedRows: IFormEditorRow[] = [];
		this.formdata.rows.forEach((row: IFormEditorRow) => {
			const control = this.formInputs.get(row.key);
			if (control && row.editable && control.value !== row.value) {
				row.newValue = control.value;
				updatedRows.push(row);
			}
		});
		if (this.formdata.callback) {
			this.formdata.callback(updatedRows);
		}
	}
}
